import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark';
export type BackgroundMode = 'color';

export interface CustomVars {
  // Primary colors
  accentRgb: string;
  bgFrom: string;
  bgTo: string;
  bgPrimary: string;
  textPrimary: string;
  // Layout
  navBg: string;
  glassBlur: number;
  cardOpacity: number;
  fontSans: string;
  panelRotate: number;
  // Facebook-style extra tokens (optional — fallback to CSS default)
  textSecondary?: string;
  textMuted?: string;
  borderColor?: string;
  bgTertiary?: string;
  cardBg?: string;
  cardBgLight?: string;
  shadowColor?: string;
  bgSecondary?: string;
  // Semantic (optional — undefined means "use CSS default")
  accentGreen?: string;
  accentYellow?: string;
  accentRed?: string;
  accentPurple?: string;
  accentBlue?: string;
  accentOrange?: string;
}

export interface ThemeCustomVars {
  dark: CustomVars | null;
  light: CustomVars | null;
}

interface UIState {
  sidebarCollapsed: boolean;
  aiPanelCollapsed: boolean;
  /** Гараар чирсэн өргөн (px); null = үндсэн. Хажуугийн самбар ба /ai-assistant тусдаа. */
  aiPanelWidth: number | null;
  aiPageWidth: number | null;
  theme: Theme;
  backgroundMode: BackgroundMode;
  customVars: ThemeCustomVars;
  themeEditorOpen: boolean;
  fullscreen: boolean;
  toggleSidebar: () => void;
  toggleAiPanel: () => void;
  setAiWidth: (page: boolean, width: number | null) => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setBackgroundMode: (mode: BackgroundMode) => void;
  setCustomVars: (mode: Theme, vars: CustomVars | null) => void;
  setThemeEditorOpen: (open: boolean) => void;
  setFullscreen: (on: boolean) => void;
  toggleFullscreen: () => void;
}

// ── Font loader ────────────────────────────────────────────────────────────
const BUNDLED_FONTS = new Set(['Space Grotesk', 'JetBrains Mono']);

function loadGoogleFont(fontName: string | undefined) {
  if (!fontName || BUNDLED_FONTS.has(fontName)) return;
  const id = `gfont-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  const encoded = fontName.replace(/\s+/g, '+');
  link.href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

// ── Helper: hex → rgba string ──────────────────────────────────────────────
function hexToRgba(hex: string, alpha: number): string | null {
  if (!hex?.startsWith('#')) return null;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (isNaN(r)) return null;
  return `rgba(${r},${g},${b},${alpha})`;
}

// ── Apply CSS custom properties ────────────────────────────────────────────
/** Every property applyCustomVars may set — cleared first so nothing from an
 *  earlier preset lingers (a field a preset leaves out falls back to the CSS default). */
const CUSTOM_PROPS = [
  '--accent-primary-rgb', '--accent-primary-dark-rgb',
  '--bg-gradient-from', '--bg-gradient-to',
  '--bg-primary', '--bg-secondary', '--overlay-bg', '--text-primary',
  '--text-secondary', '--text-muted',
  '--border-color', '--bg-tertiary', '--hover-overlay',
  '--nav-bg', '--glass-blur', '--card-bg', '--card-bg-light',
  '--shadow-color', '--glass-shadow',
  '--font-sans', '--bd-panel-rotate',
  '--accent-green', '--accent-yellow', '--accent-red', '--accent-purple', '--accent-blue', '--accent-orange',
];

export function applyCustomVars(vars: CustomVars | null, mode: Theme = 'dark') {
  const root = document.documentElement;
  CUSTOM_PROPS.forEach((p) => root.style.removeProperty(p));
  if (!vars) return;
  const set = (prop: string, value: string | undefined | null) => { if (value) root.style.setProperty(prop, value); };

  set('--accent-primary-rgb', vars.accentRgb);
  set('--accent-primary-dark-rgb', vars.accentRgb);
  set('--bg-gradient-from', vars.bgFrom);
  set('--bg-gradient-to', vars.bgTo);
  set('--bg-primary', vars.bgPrimary);
  set('--overlay-bg', hexToRgba(vars.bgPrimary, 0.92));
  set('--text-primary', vars.textPrimary);
  set('--nav-bg', vars.navBg);
  if (vars.glassBlur != null) set('--glass-blur', `blur(${vars.glassBlur}px)`);
  if (vars.panelRotate != null) set('--bd-panel-rotate', `${vars.panelRotate}deg`);
  if (vars.fontSans) {
    set('--font-sans', `'${vars.fontSans}', system-ui, sans-serif`);
    loadGoogleFont(vars.fontSans);
  }

  set('--text-secondary', vars.textSecondary);
  // Muted is its own step below secondary — aliasing it to secondary (as this
  // used to) flattened labels vs values, so even "Анхны утга" lost contrast.
  set('--text-muted', vars.textMuted);
  set('--border-color', vars.borderColor);
  set('--bg-tertiary', vars.bgTertiary);
  // Hover is a translucent wash of the text colour, not a solid fill.
  set('--hover-overlay', hexToRgba(vars.textPrimary, mode === 'dark' ? 0.08 : 0.05));
  set('--card-bg', vars.cardBg);
  set('--card-bg-light', vars.cardBgLight);
  set('--bg-secondary', vars.bgSecondary);
  if (vars.shadowColor) {
    set('--shadow-color', vars.shadowColor);
    set('--glass-shadow', `0 2px 8px ${vars.shadowColor}`);
  }

  set('--accent-green', vars.accentGreen);
  set('--accent-yellow', vars.accentYellow);
  set('--accent-red', vars.accentRed);
  set('--accent-purple', vars.accentPurple);
  set('--accent-blue', vars.accentBlue);
  set('--accent-orange', vars.accentOrange);
}

// ── Theme class ────────────────────────────────────────────────────────────
function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle('light', theme === 'light');
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

function applyBackgroundMode(mode: BackgroundMode) {
  void mode;
  document.documentElement.classList.remove('bg-image-mode');
}

// ── Store ──────────────────────────────────────────────────────────────────
export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      aiPanelCollapsed: true,
      aiPanelWidth: null,
      aiPageWidth: null,
      theme: 'dark',
      backgroundMode: 'color',
      customVars: { dark: null, light: null },
      themeEditorOpen: false,
      fullscreen: false,
      // Дашбоордын газрын зураг хараахан ашиглахад бэлэн биш (байршлын
      // өгөгдөл дутуу) тул ҮНДСЭН ТӨЛӨВӨӨР УНТРААЛТТАЙ. Код нь хэвээр —
      // өгөгдөл бүрдмэгц `ThemeEditor`-ийн товчийг сэргээхэд л хангалттай.
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      toggleAiPanel: () => set((s) => ({ aiPanelCollapsed: !s.aiPanelCollapsed })),
      setAiWidth: (page, width) => set(page ? { aiPageWidth: width } : { aiPanelWidth: width }),
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => {
        const next = get().theme === 'dark' ? 'light' : 'dark';
        set({ theme: next });
      },
      setBackgroundMode: (_mode) => set({ backgroundMode: 'color' }),
      setCustomVars: (mode, vars) => {
        const prev = get().customVars;
        set({ customVars: { ...prev, [mode]: vars } });
        if (get().theme === mode) applyCustomVars(vars, mode);
      },
      setThemeEditorOpen: (open) => set({ themeEditorOpen: open }),
      setFullscreen: (on) => set({ fullscreen: on }),
      toggleFullscreen: () => set((s) => ({ fullscreen: !s.fullscreen })),
    }),
    {
      name: 'ui-store',
      // v2: the dashboard map was removed — drop its saved flag.
      version: 2,
      migrate: (state) => {
        const s = (state ?? {}) as Record<string, unknown>;
        delete s.dashboardMapVisible;
        return s as never;
      },
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        aiPanelCollapsed: s.aiPanelCollapsed,
        aiPanelWidth: s.aiPanelWidth,
        aiPageWidth: s.aiPageWidth,
        theme: s.theme,
        backgroundMode: s.backgroundMode,
        customVars: s.customVars,
        fullscreen: s.fullscreen,
      }),
    }
  )
);

// Re-apply when theme switches
let prevTheme: Theme | undefined;
useUIStore.subscribe((state) => {
  if (state.theme !== prevTheme) {
    prevTheme = state.theme;
    applyTheme(state.theme);
    const vars = state.theme === 'dark' ? state.customVars.dark : state.customVars.light;
    applyCustomVars(vars, state.theme);
  }
});

let prevBackgroundMode: BackgroundMode | undefined;
useUIStore.subscribe((state) => {
  if (state.backgroundMode !== prevBackgroundMode) {
    prevBackgroundMode = state.backgroundMode;
    applyBackgroundMode(state.backgroundMode);
  }
});

// Apply on initial load / rehydration
const { theme, customVars } = useUIStore.getState();
applyTheme(theme);
const initVars = theme === 'dark' ? customVars.dark : customVars.light;
applyCustomVars(initVars, theme);
if (initVars?.fontSans) loadGoogleFont(initVars.fontSans);
applyBackgroundMode(useUIStore.getState().backgroundMode);
